import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Trash2, ShoppingCart, ArrowLeft, ShieldCheck,
    CreditCard, Globe, Zap, Package, Tag, X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotification } from '../contexts/NotificationContext';
import { supabase } from '../lib/supabase';

const Basket = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { language } = useLanguage();
    const { cartItems, cartTotal, removeFromCart, clearCart } = useCart();
    const { showModal } = useNotification();
    const [promoCode, setPromoCode] = useState('');
    const [appliedPromo, setAppliedPromo] = useState(null);
    const [isApplyingPromo, setIsApplyingPromo] = useState(false);

    const handleApplyPromo = async () => {
        setIsApplyingPromo(true);

        try {
            // Fetch promo code from database
            const { data, error } = await supabase
                .from('promo_codes')
                .select('*')
                .eq('code', promoCode.toUpperCase())
                .eq('is_active', true)
                .single();

            if (error || !data) {
                showModal(
                    language === 'tr' ? 'Geçersiz Kod' : 'Invalid Code',
                    language === 'tr' ? 'Bu promosyon kodu geçerli değil.' : 'This promo code is not valid.',
                    'error'
                );
                setIsApplyingPromo(false);
                return;
            }

            // Check if code is expired
            if (data.valid_until && new Date(data.valid_until) < new Date()) {
                showModal(
                    language === 'tr' ? 'Süresi Dolmuş' : 'Expired',
                    language === 'tr' ? 'Bu promosyon kodunun süresi dolmuş.' : 'This promo code has expired.',
                    'error'
                );
                setIsApplyingPromo(false);
                return;
            }

            // Check if code has reached max uses
            if (data.max_uses && data.current_uses >= data.max_uses) {
                showModal(
                    language === 'tr' ? 'Kullanım Limiti' : 'Usage Limit',
                    language === 'tr' ? 'Bu promosyon kodu kullanım limitine ulaşmış.' : 'This promo code has reached its usage limit.',
                    'error'
                );
                setIsApplyingPromo(false);
                return;
            }

            setAppliedPromo({
                code: data.code,
                discount: data.discount_value,
                type: data.discount_type
            });

            showModal(
                language === 'tr' ? 'Promosyon Kodu Uygulandı' : 'Promo Code Applied',
                language === 'tr' ? 'İndirim başarıyla uygulandı!' : 'Discount applied successfully!',
                'success'
            );
        } catch (error) {
            console.error('Promo code error:', error);
            showModal(
                language === 'tr' ? 'Hata' : 'Error',
                language === 'tr' ? 'Promosyon kodu kontrol edilirken bir hata oluştu.' : 'An error occurred while checking the promo code.',
                'error'
            );
        } finally {
            setIsApplyingPromo(false);
        }
    };

    const handleRemovePromo = () => {
        setAppliedPromo(null);
        setPromoCode('');
    };

    const calculateDiscount = () => {
        if (!appliedPromo) return 0;
        if (appliedPromo.type === 'percentage') {
            return cartTotal * appliedPromo.discount;
        }
        return appliedPromo.discount;
    };

    const discount = calculateDiscount();
    const finalTotal = Math.max(0, cartTotal - discount);

    const handleProceedToCheckout = () => {
        if (!user) {
            showModal(
                language === 'tr' ? 'Giriş Gerekli' : 'Login Required',
                language === 'tr' ? 'Ödeme işlemine devam etmek için lütfen giriş yapın.' : 'Please login to proceed with checkout.',
                'info'
            );
            navigate('/auth/login');
            return;
        }

        if (cartItems.length === 0) return;

        // Navigate to checkout page with order details
        navigate('/checkout', {
            state: {
                amount: finalTotal,
                items: cartItems,
                discount: discount,
                appliedPromo: appliedPromo
            }
        });
    };

    const emptyCartText = language === 'tr' ? 'Sepetiniz boş' : 'Your cart is empty';
    const totalText = language === 'tr' ? 'Toplam' : 'Total';
    const continueText = language === 'tr' ? 'Alışverişe Devam Et' : 'Continue Shopping';

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-32 pb-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        {language === 'tr' ? 'Geri Dön' : 'Back'}
                    </button>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                        <ShoppingCart className="w-8 h-8 text-blue-600" />
                        {language === 'tr' ? 'Sepetim' : 'My Cart'}
                    </h1>
                </div>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Cart Items */}
                    <div className="lg:col-span-2 space-y-4">
                        <AnimatePresence mode="popLayout">
                            {cartItems.length === 0 ? (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="bg-white dark:bg-gray-900 rounded-3xl p-12 text-center border border-gray-100 dark:border-gray-800"
                                >
                                    <div className="w-20 h-20 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
                                        <ShoppingCart className="w-10 h-10 text-gray-400" />
                                    </div>
                                    <p className="text-xl text-gray-500 dark:text-gray-400 mb-8">{emptyCartText}</p>
                                    <button
                                        onClick={() => navigate('/solutions')}
                                        className="inline-flex items-center px-8 py-4 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-700 transition-all hover:shadow-xl hover:-translate-y-0.5"
                                    >
                                        {continueText}
                                    </button>
                                </motion.div>
                            ) : (
                                cartItems.map((item) => (
                                    <motion.div
                                        key={item.id}
                                        layout
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: 20 }}
                                        className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-100 dark:border-gray-800 group hover:border-blue-500/30 transition-all shadow-sm"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-4">
                                                <div className={`p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-600`}>
                                                    {item.type === 'domain' ? <Globe className="w-6 h-6" /> : <Package className="w-6 h-6" />}
                                                </div>
                                                <div>
                                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">{item.name}</h3>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">{item.type}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-6">
                                                <div className="text-right">
                                                    <p className="text-xl font-bold text-blue-600">${item.price}</p>
                                                    <p className="text-xs text-gray-500">{item.type === 'domain' ? (language === 'tr' ? '/ yıl' : '/ year') : (language === 'tr' ? '/ ay' : '/ month')}</p>
                                                </div>
                                                <button
                                                    onClick={() => removeFromCart(item.id)}
                                                    className="p-3 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                                                >
                                                    <Trash2 className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Summary */}
                    <div className="lg:col-span-1">
                        <div className="bg-white dark:bg-gray-900 rounded-3xl p-8 border border-gray-100 dark:border-gray-800 sticky top-32 shadow-sm">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                                {language === 'tr' ? 'Sipariş Özeti' : 'Order Summary'}
                            </h2>

                            {/* Promo Code Section */}
                            <div className="mb-6 pb-6 border-b border-gray-100 dark:border-gray-800">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {language === 'tr' ? 'Promosyon Kodu' : 'Promo Code'}
                                </label>
                                {appliedPromo ? (
                                    <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-xl">
                                        <div className="flex items-center gap-2">
                                            <Tag className="w-4 h-4 text-green-600" />
                                            <span className="text-sm font-bold text-green-600">{appliedPromo.code}</span>
                                        </div>
                                        <button
                                            onClick={handleRemovePromo}
                                            className="text-green-600 hover:text-green-700"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={promoCode}
                                            onChange={(e) => setPromoCode(e.target.value)}
                                            placeholder={language === 'tr' ? 'Kodu girin' : 'Enter code'}
                                            className="flex-1 px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none dark:text-white text-sm"
                                        />
                                        <button
                                            onClick={handleApplyPromo}
                                            disabled={!promoCode || isApplyingPromo}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                        >
                                            {isApplyingPromo ? '...' : (language === 'tr' ? 'Uygula' : 'Apply')}
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-4 mb-8">
                                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                                    <span>{language === 'tr' ? 'Ara Toplam' : 'Subtotal'}</span>
                                    <span>${cartTotal.toFixed(2)}</span>
                                </div>
                                {appliedPromo && (
                                    <div className="flex justify-between text-green-600">
                                        <span>{language === 'tr' ? 'İndirim' : 'Discount'}</span>
                                        <span>-${discount.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                                    <span>{language === 'tr' ? 'Vergiler' : 'Taxes'}</span>
                                    <span>$0.00</span>
                                </div>
                                <div className="pt-4 border-t border-gray-100 dark:border-gray-800 flex justify-between items-center">
                                    <span className="text-lg font-bold text-gray-900 dark:text-white">{totalText}</span>
                                    <span className="text-3xl font-bold text-blue-600">${finalTotal.toFixed(2)}</span>
                                </div>
                            </div>

                            <button
                                onClick={handleProceedToCheckout}
                                disabled={cartItems.length === 0}
                                className="w-full py-5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl font-bold hover:shadow-2xl hover:shadow-blue-500/20 transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                            >
                                <CreditCard className="w-6 h-6" />
                                {language === 'tr' ? 'Ödemeye Geç' : 'Proceed to Payment'}
                            </button>

                            <div className="mt-8 space-y-4">
                                <div className="flex items-center gap-3 text-sm text-gray-500">
                                    <ShieldCheck className="w-5 h-5 text-green-500" />
                                    <span>{language === 'tr' ? 'Güvenli Ödeme' : 'Secure Payment'}</span>
                                </div>
                                <div className="flex items-center gap-3 text-sm text-gray-500">
                                    <Zap className="w-5 h-5 text-yellow-500" />
                                    <span>{language === 'tr' ? 'Anında Aktivasyon' : 'Instant Activation'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Basket;
